import { NavLink, Outlet, useNavigate } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import {
  LayoutDashboard, Receipt, ScrollText, Building2, Key, Globe, Webhook, WalletCards, CreditCard,
  ShieldCheck, LogOut, ChevronDown, User as UserIcon, Users as UsersIcon,
  Undo2, Settings as SettingsIcon, Volume2, VolumeX, FileText, UserCog, MessageSquare
} from "lucide-react";
import { useMemo, useState } from "react";
import { isSoundEnabled, setSoundEnabled, sounds } from "@/lib/sound";
import Logo from "@/components/Logo";
import { Menu, X } from "lucide-react";


const NAV = [
  { to: "/dashboard", icon: LayoutDashboard, label: "Overview", end: true, testid: "nav-overview" },
  { to: "/dashboard/payment-links", icon: Receipt, label: "Payment Links", testid: "nav-payment-links" },
  { to: "/dashboard/mandates", icon: CreditCard, label: "Mandates", testid: "nav-mandates" },
  { to: "/dashboard/installment-history", icon: ScrollText, label: "Installment History", testid: "nav-installment-history" },
  { to: "/dashboard/manual-payments", icon: WalletCards, label: "Manual Payments", testid: "nav-manual-payments" },
  { to: "/dashboard/payments", icon: ScrollText, label: "Payments Log", testid: "nav-payments" },
  { to: "/dashboard/customers", icon: UsersIcon, label: "Customers", testid: "nav-customers" },
  { to: "/dashboard/refunds", icon: Undo2, label: "Refunds", testid: "nav-refunds" },
  { to: "/dashboard/reports", icon: FileText, label: "Reports", testid: "nav-reports" },
  { to: "/dashboard/feedback", icon: MessageSquare, label: "Feedback", testid: "nav-feedback" },
  { to: "/dashboard/branches", icon: Building2, label: "Branches", testid: "nav-branches" },
  { to: "/dashboard/websites", icon: Globe, label: "Websites", testid: "nav-websites", adminOnly: true },
  { to: "/dashboard/api-keys", icon: Key, label: "API Keys", testid: "nav-api-keys", adminOnly: true },
  { to: "/dashboard/webhooks", icon: Webhook, label: "Webhook Logs", testid: "nav-webhooks", adminOnly: true },
  { to: "/dashboard/audit", icon: ShieldCheck, label: "Audit Logs", testid: "nav-audit", adminOnly: true },
  { to: "/dashboard/roles", icon: UserCog, label: "Roles & Permissions", testid: "nav-roles", adminOnly: true },
  { to: "/dashboard/settings", icon: SettingsIcon, label: "Settings", testid: "nav-settings", adminOnly: true },
];

export default function AdminLayout() {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const [openMenu, setOpenMenu] = useState(false);
  const [mobileMenu, setMobileMenu] = useState(false);
  const [soundOn, setSoundOn] = useState(isSoundEnabled());

  const items = useMemo(
    () => NAV.filter(i => !i.adminOnly || user?.role === "admin"),
    [user?.role]
  );

  const toggleSound = () => {
    const v = !soundOn;
    setSoundOn(v);
    setSoundEnabled(v);
    if (v) sounds.info();
  };

  return (
    <div className="relative min-h-screen">
      <div className="relative z-10 flex">
        
        <aside
          className={`
            fixed inset-y-0 left-0 z-50
            w-72
            bg-[#090909]
            border-r border-white/10
            backdrop-blur-xl
            transform transition-transform duration-500 ease-[cubic-bezier(.22,1,.36,1)]

            ${mobileMenu ? "translate-x-0 flex" : "-translate-x-full"}

            md:translate-x-0
            md:flex

            flex-col
            p-4
          `}
        >

          <div className="flex items-center gap-3 px-2 py-2">
            <div className="flex items-center gap-3 pb-6 border-b border-white/10">
                <Logo size={42}/>
                <div>
                    <div className="font-semibold text-white">
                        LostDRKC
                    </div>
            
                    <div className="text-xs text-white/50">
                        Payments
                    </div>
                </div>
            </div>
          </div>

          <nav className="mt-8 flex-1 space-y-2">
            {items.map((i) => (
              
              <NavLink
                key={i.to}
                to={i.to}
                end={i.end}
                onClick={() => setMobileMenu(false)}
                data-testid={i.testid}
                className={({ isActive }) =>
                  `group flex items-center gap-3 px-3 py-2.5 rounded-md text-sm transition-all duration-300 ${
                    isActive
                      ? "bg-[#fbce18]/10 text-[#fbce18] border border-[#fbce18]/30 shadow-[0_0_20px_rgba(251,206,24,0.15)]"
                      : "text-white/60 hover:text-white hover:bg-white/5 border border-transparent"
                  }`
                }
              >
                <i.icon
                  size={18}
                  className="transition-transform duration-300 group-hover:scale-110"
                />

                <span>{i.label}</span>
              </NavLink>

            ))}
          </nav>

          <div className="mt-4 ldc-glass p-3">
            <div className="text-[10px] tracking-[0.24em] uppercase text-white/40">Signed in</div>
            <div className="mt-1 text-sm truncate">{user?.name}</div>
            <div className="text-xs text-white/40 truncate">{user?.email}</div>
            <button
              onClick={logout}
              data-testid="admin-logout-btn"
              className="mt-3 w-full text-xs py-2 rounded border border-white/10 hover:border-[#fbce18]/40 inline-flex items-center justify-center gap-2 text-white/70 hover:text-white transition-colors"
            >
              <LogOut size={14} /> Sign out
            </button>
          </div>
        </aside>
        
        {mobileMenu && (
          <div
            className="fixed inset-0 z-40 md:hidden bg-black/70 backdrop-blur-sm"
            onClick={() => setMobileMenu(false)}
          />
        )}

<main className="flex-1 md:ml-72 ml-0">
  <header className="sticky top-0 z-30 bg-black/60 backdrop-blur-xl border-b border-white/5">
    <div className="flex items-center justify-between px-4 md:px-10 h-16">

      {/* Left */}
      <div className="flex items-center gap-3">

        <button
          onClick={() => setMobileMenu(!mobileMenu)}
          className="md:hidden p-2 rounded-md border border-white/10 hover:border-[#fbce18]/40 hover:bg-white/5 transition-colors text-white"
        >
          {mobileMenu ? <X size={20} /> : <Menu size={20} />}
        </button>

        <div>

          <div className="text-[10px] tracking-[0.28em] uppercase text-white/40">
            Admin Console
          </div>


          <div className="text-sm text-white/70">
            Welcome back, {user?.name?.split(" ")[0]}
          </div>
        </div>

      </div>

      {/* Right */}
      <div className="flex items-center gap-3">

        <button
          onClick={toggleSound}
          className="p-2 rounded-md border border-white/10 hover:border-[#fbce18]/40 transition-colors text-white/70"
          title={soundOn ? "Sounds on" : "Sounds off"}
          data-testid="admin-sound-toggle"
        >
          {soundOn ? <Volume2 size={16}/> : <VolumeX size={16}/>}
        </button>

        <span className="hidden sm:inline-flex ldc-badge ldc-badge-pending">
          {user?.role?.toUpperCase()}
        </span>

        <div className="relative">

          <button
            onClick={() => setOpenMenu(!openMenu)}
            className="flex items-center gap-2 px-3 py-2 rounded-md border border-white/10 hover:border-[#fbce18]/40 transition-colors"
            data-testid="admin-user-menu"
          >
            <div className="w-7 h-7 rounded-full bg-[#fbce18]/20 text-[#fbce18] flex items-center justify-center text-xs font-semibold">
              {user?.name?.[0]?.toUpperCase()}
            </div>

            <ChevronDown size={14} className="text-white/60" />
          </button>

          {openMenu && (
            <div className="absolute right-0 mt-2 w-56 ldc-glass p-2 z-30">

              <button
                className="w-full text-left px-3 py-2 rounded hover:bg-white/5 text-sm inline-flex items-center gap-2"
                onClick={() => {
                  nav("/customer");
                  setOpenMenu(false);
                  setMobileMenu(false);
                }}
                data-testid="admin-goto-customer"
              >
                <UserIcon size={14} />
                Customer view
              </button>

              <button
                className="w-full text-left px-3 py-2 rounded hover:bg-white/5 text-sm inline-flex items-center gap-2 text-white/80"
                onClick={logout}
                data-testid="admin-menu-logout"
              >
                <LogOut size={14} />
                Sign out
              </button>

            </div>
          )}

        </div>

      </div>

    </div>
  </header>

  <div className="px-4 md:px-10 py-8">
    <Outlet />
  </div>
</main>


      </div>
    </div>
  );
}
